import PropTypes from 'prop-types';
import Passport from '@mos-front-components/public/es/components/Passport';
import { createStructuredSelector } from 'reselect';

import {
  completeSTEPAPPLICANTTYPESelector,
  completeSTEPAPPLICANTSelector,
} from '../../store/step/selectors';
import { applicantChildSelector } from '../../store/applicant/selectors';

import {
  mainDataValuesSelector,
  mainDataEditableFieldsSelector,
  mainDataErrorsSelector,
  mainDataIsValidSelector,
  mainDataBlurSelector,
  mainDataChildrenSelector,
  getBirthdateBeforeFourteenAndHalfAgo,
  getUnderFourteenSelector,
  mainDataDisabledSelector,
} from '../../store/mainData/selectors';

import {
  mainDataValuesSelector as mainDataValuesChildSelector,
  mainDataEditableFieldsSelector as mainDataEditableFieldsChildSelector,
  mainDataErrorsSelector as mainDataErrorsChildSelector,
  mainDataBlurSelector as mainDataBlurChildSelector,
  mainDataIsValidSelector as mainDataIsValidChildSelector,
  getBirthdateBeforeFourteenAndHalfAgo as getChildBirthdateBeforeFourteenAndHalfAgo,
} from '../../store/mainDataChild/selectors';

import {
  passportValuesSelector,
  passportErrorsSelector,
  passportEditableFieldsSelector,
  passportDisabledSelector,
  passportBlurSelector,
} from '../../store/passport/selectors';

import {
  passportValuesSelector as passportValuesChildSelector,
  passportErrorsSelector as passportErrorsChildSelector,
  passportEditableFieldsSelector as passportEditableFieldsChildSelector,
  passportDisabledSelector as passportDisabledChildSelector,
  passportBlurSelector as passportBlurChildSelector,
} from '../../store/passportChild/selectors';
import {
  actRecordBlurSelector,
  actRecordDataSelector,
  actRecordRegPlaceNameSuggestionsFetchingErrorSelector,
  actRecordRegPlaceNameSuggestionsFetchingSelector,
  actRecordRegPlaceNameSuggestionsSelector,
} from '../../store/actRecord/selectors';
import { ACT_RECORD_FIELD_NAMES } from '../../constants/actRecord';

export const stepPersonStructuredSelector = createStructuredSelector({
  stepApplicantTypeComplete: completeSTEPAPPLICANTTYPESelector,
  mainDataValues: mainDataValuesSelector,
  mainDataEditableFields: mainDataEditableFieldsSelector,
  mainDataErrors: mainDataErrorsSelector,
  mainDataValid: mainDataIsValidSelector,
  mainDataDisabled: mainDataDisabledSelector,
  passportValues: passportValuesSelector,
  passportEditableFields: passportEditableFieldsSelector,
  passportErrors: passportErrorsSelector,
  passportDisabled: passportDisabledSelector,
  mainDataBlur: mainDataBlurSelector,
  passportBlur: passportBlurSelector,
  actRecordData: actRecordDataSelector,
  actRecordBlur: actRecordBlurSelector,
  actRecordSuggestions: actRecordRegPlaceNameSuggestionsSelector,
  actRecordSuggestionsFetching:
    actRecordRegPlaceNameSuggestionsFetchingSelector,
  actRecordSuggestionsFetchingError:
    actRecordRegPlaceNameSuggestionsFetchingErrorSelector,
  personChildren: mainDataChildrenSelector,
  birthdateBeforeFourteenAndHalf: getBirthdateBeforeFourteenAndHalfAgo,
  underFourteen: getUnderFourteenSelector,
});

export const stepPersonChildStructuredSelector = createStructuredSelector({
  stepApplicantTypeComplete: completeSTEPAPPLICANTTYPESelector,
  stepApplicantComplete: completeSTEPAPPLICANTSelector,
  mainDataValues: mainDataValuesChildSelector,
  mainDataEditableFields: mainDataEditableFieldsChildSelector,
  mainDataErrors: mainDataErrorsChildSelector,
  mainDataDisabled: mainDataDisabledSelector,
  passportValues: passportValuesChildSelector,
  passportEditableFields: passportEditableFieldsChildSelector,
  passportErrors: passportErrorsChildSelector,
  passportDisabled: passportDisabledChildSelector,
  mainDataBlur: mainDataBlurChildSelector,
  passportBlur: passportBlurChildSelector,
  actRecordData: actRecordDataSelector,
  actRecordBlur: actRecordBlurSelector,
  actRecordSuggestions: actRecordRegPlaceNameSuggestionsSelector,
  actRecordSuggestionsFetching:
    actRecordRegPlaceNameSuggestionsFetchingSelector,
  actRecordSuggestionsFetchingError:
    actRecordRegPlaceNameSuggestionsFetchingErrorSelector,
  child: applicantChildSelector,
  personChildren: mainDataChildrenSelector,
  mainDataValid: mainDataIsValidChildSelector,
  birthdateBeforeFourteenAndHalf: getChildBirthdateBeforeFourteenAndHalfAgo,
});

const fieldPropType = PropTypes.shape({
  value: PropTypes.string,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
});

export const stepPersonStructuredSelectorPropTypes = {
  mainDataValues: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    middleName: PropTypes.string,
    gender: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    birthdate: PropTypes.string,
    citizenship: PropTypes.string,
  }),
  mainDataEditableFields: PropTypes.shape({
    firstName: PropTypes.bool,
    lastName: PropTypes.bool,
    middleName: PropTypes.bool,
    gender: PropTypes.bool,
    phone: PropTypes.bool,
    email: PropTypes.bool,
    birthdate: PropTypes.bool,
  }),
  mainDataErrors: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    middleName: PropTypes.string,
    gender: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    birthdate: PropTypes.string,
  }),
  mainDataValid: PropTypes.bool,
  passportValues: PropTypes.shape({
    documentType: PropTypes.string,
    byWhom: PropTypes.string,
    dateOfIssue: PropTypes.string,
    number: PropTypes.string,
  }),
  passportEditableFields: PropTypes.arrayOf(
    PropTypes.oneOf(Object.values(Passport.FIELD_NAME)),
  ),
  passportErrors: PropTypes.shape({
    byWhom: PropTypes.string,
    dateOfIssue: PropTypes.string,
    number: PropTypes.string,
  }),
  passportDisabled: PropTypes.bool.isRequired,
  mainDataBlur: PropTypes.shape({
    firstName: PropTypes.bool,
    lastName: PropTypes.bool,
    middleName: PropTypes.bool,
    gender: PropTypes.bool,
    phone: PropTypes.bool,
    email: PropTypes.bool,
    birthdate: PropTypes.bool,
  }),
  passportBlur: PropTypes.shape({
    documentType: PropTypes.bool,
    byWhom: PropTypes.bool,
    dateOfIssue: PropTypes.bool,
    number: PropTypes.bool,
  }),

  actRecordData: PropTypes.shape({
    [ACT_RECORD_FIELD_NAMES.date]: fieldPropType,
    [ACT_RECORD_FIELD_NAMES.number]: fieldPropType,
    [ACT_RECORD_FIELD_NAMES.regPlace]: fieldPropType,
  }),
  actRecordBlur: PropTypes.shape({
    [ACT_RECORD_FIELD_NAMES.date]: PropTypes.bool,
    [ACT_RECORD_FIELD_NAMES.number]: PropTypes.bool,
    [ACT_RECORD_FIELD_NAMES.regPlace]: PropTypes.bool,
  }),
  actRecordSuggestions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      code: PropTypes.string,
    }),
  ),
  actRecordSuggestionsFetching: PropTypes.bool,
  actRecordSuggestionsFetchingError: PropTypes.bool,

  birthdateBeforeFourteenAndHalf: PropTypes.bool,
  personChildren: PropTypes.array,
  underFourteen: PropTypes.bool,
};
